/**
 * SchoolReportsAnalytics — Phase F
 * Tabs: Executive · Attendance Heatmap · Students at Risk v2 · Teacher Performance · Exports
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Loader2, BarChart3, Users, GraduationCap, AlertTriangle, TrendingUp, FileDown,
  Activity, FileText, Thermometer, Printer
} from 'lucide-react';
import api from '@/lib/api';
import { getToken } from '@/lib/authToken';

// ========== EXECUTIVE DASHBOARD ==========
const ExecutiveTab = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['rep-executive', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/reports/executive`).then(r => r.data),
  });
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const d = data || {};
  const Section = ({ title, icon: Icon, color, items }) => (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-sm"><Icon className={`h-4 w-4 ${color}`} /> {title}</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-2">
          {items.map((it) => (
            <div key={it.key} className="p-2 rounded border bg-slate-50" data-testid={`exec-${it.key}`}>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{it.label}</div>
              <div className="text-xl font-bold text-slate-900">{it.value}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="exec-dashboard">
      <Section title="People" icon={Users} color="text-indigo-600" items={[
        { key: 'students_active', label: 'Students', value: d.people?.students_active || 0 },
        { key: 'staff_active', label: 'Staff', value: d.people?.staff_active || 0 },
        { key: 'teachers_active', label: 'Teachers', value: d.people?.teachers_active || 0 },
        { key: 'classes', label: 'Classes', value: d.people?.classes || 0 },
      ]} />
      <Section title="Academics" icon={GraduationCap} color="text-emerald-600" items={[
        { key: 'syllabus_percent', label: 'Syllabus %', value: (d.academics?.syllabus_percent || 0) + '%' },
        { key: 'lessons_taught', label: 'Lessons Taught', value: d.academics?.lessons_taught || 0 },
        { key: 'live_sessions_30d', label: 'Live 30d', value: d.academics?.live_sessions_30d || 0 },
        { key: 'exams_30d', label: 'Exams 30d', value: d.academics?.exams_30d || 0 },
      ]} />
      <Section title="Attendance 30d" icon={Activity} color="text-amber-600" items={[
        { key: 'student_percent_30d', label: 'Student %', value: (d.attendance?.student_percent_30d || 0) + '%' },
        { key: 'staff_percent_30d', label: 'Staff %', value: (d.attendance?.staff_percent_30d || 0) + '%' },
        { key: 'student_records_30d', label: 'Student rows', value: d.attendance?.student_records_30d || 0 },
        { key: 'staff_records_30d', label: 'Staff rows', value: d.attendance?.staff_records_30d || 0 },
      ]} />
      <Section title="Risk" icon={AlertTriangle} color="text-rose-600" items={[
        { key: 'students_at_risk', label: 'Students', value: d.risk?.students_at_risk || 0 },
        { key: 'staff_at_risk', label: 'Staff', value: d.risk?.staff_at_risk || 0 },
        { key: 'high_severity_alerts', label: 'High-sev', value: d.risk?.high_severity_alerts || 0 },
        { key: 'gen', label: 'As of', value: (d.generated_at || '').slice(11, 16) },
      ]} />
      <Section title="Communication 30d" icon={TrendingUp} color="text-sky-600" items={[
        { key: 'messages_30d', label: 'Queued', value: d.communication?.messages_30d || 0 },
        { key: 'delivered_30d', label: 'Delivered', value: d.communication?.delivered_30d || 0 },
        { key: 'rate_30d', label: 'Rate %', value: (d.communication?.delivery_rate_percent || 0) + '%' },
        { key: 'graduates', label: 'Graduates', value: d.people?.students_graduated || 0 },
      ]} />
    </div>
  );
};

// ========== ATTENDANCE HEATMAP ==========
const HeatmapTab = ({ tenantId }) => {
  const [scope, setScope] = useState('student');
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ['rep-heatmap', tenantId, scope, days],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/reports/attendance-heatmap?scope=${scope}&days=${days}`).then(r => r.data),
  });
  const colorFor = (p) => {
    if (p === null || p === undefined) return 'bg-slate-100';
    if (p >= 90) return 'bg-emerald-500';
    if (p >= 75) return 'bg-emerald-300';
    if (p >= 60) return 'bg-amber-300';
    if (p >= 40) return 'bg-orange-400';
    return 'bg-rose-500';
  };
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2 text-base"><Thermometer className="h-4 w-4 text-rose-600" /> Attendance Heatmap</CardTitle>
          <CardDescription>Green = high presence · Red = low. Hover cells for %.</CardDescription>
        </div>
        <div className="flex gap-2">
          <Select value={scope} onValueChange={setScope}><SelectTrigger className="w-32" data-testid="heatmap-scope"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="student">Students</SelectItem><SelectItem value="staff">Staff</SelectItem></SelectContent></Select>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}><SelectTrigger className="w-24" data-testid="heatmap-days"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="7">7d</SelectItem><SelectItem value="14">14d</SelectItem><SelectItem value="30">30d</SelectItem></SelectContent></Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
         (data?.rows || []).length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-testid="heatmap-empty">No data for this scope.</p> :
         <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="text-left pr-3 pb-1 sticky left-0 bg-white z-10">Row</th>
                {data.days.map((d) => <th key={d} className="px-1 pb-1 text-[10px] text-slate-500 font-normal">{d.slice(5)}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.key}>
                  <td className="pr-3 py-0.5 font-medium sticky left-0 bg-white z-10 text-[11px]">{r.name}</td>
                  {r.cells.map((c) => (
                    <td key={c.date} className="p-0.5">
                      <div className={`w-8 h-6 rounded ${colorFor(c.percent)} flex items-center justify-center`} title={`${c.date}: ${c.percent === null ? 'no data' : c.percent + '%'}`} data-testid={`heatmap-cell-${r.key}-${c.date}`}>
                        <span className={`text-[9px] ${c.percent >= 60 ? 'text-white' : 'text-slate-700'}`}>{c.percent === null ? '' : c.percent}</span>
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
         </div>}
      </CardContent>
    </Card>
  );
};

// ========== STUDENTS AT RISK V2 ==========
const StudentsAtRiskTab = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['rep-sar-v2', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/reports/students-at-risk-v2`).then(r => r.data),
  });
  const tierColor = { critical: 'bg-rose-600 text-white', at_risk: 'bg-amber-500 text-white', watch: 'bg-sky-400 text-white', on_track: 'bg-emerald-500 text-white' };
  const d = data || {};
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border bg-rose-50"><div className="text-xs text-rose-700">Critical</div><div className="text-2xl font-bold text-rose-900" data-testid="sar-critical">{d.by_tier?.critical || 0}</div></div>
        <div className="p-3 rounded-lg border bg-amber-50"><div className="text-xs text-amber-700">At Risk</div><div className="text-2xl font-bold text-amber-900" data-testid="sar-atrisk">{d.by_tier?.at_risk || 0}</div></div>
        <div className="p-3 rounded-lg border bg-sky-50"><div className="text-xs text-sky-700">Watch</div><div className="text-2xl font-bold text-sky-900" data-testid="sar-watch">{d.by_tier?.watch || 0}</div></div>
        <div className="p-3 rounded-lg border bg-emerald-50"><div className="text-xs text-emerald-700">On Track</div><div className="text-2xl font-bold text-emerald-900" data-testid="sar-ontrack">{d.by_tier?.on_track || 0}</div></div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Student Risk Scores</CardTitle>
          <CardDescription>Weighted composite: 35% attendance + 25% engagement + 25% exam + 15% pop-quiz compliance.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
           (d.students || []).length === 0 ? <p className="text-sm text-slate-500 text-center py-6" data-testid="sar-empty">No student data yet.</p> :
           <Table>
            <TableHeader><TableRow><TableHead>Adm #</TableHead><TableHead>Student</TableHead><TableHead>Score</TableHead><TableHead>Tier</TableHead><TableHead>Att%</TableHead><TableHead>Eng</TableHead><TableHead>Exam%</TableHead><TableHead>Pending Q</TableHead></TableRow></TableHeader>
            <TableBody>
              {(d.students || []).slice(0, 100).map(s => (
                <TableRow key={s.student_id} data-testid={`sar-row-${s.student_id}`}>
                  <TableCell className="text-xs">{s.admission_number}</TableCell>
                  <TableCell className="font-medium text-sm">{s.student_name}</TableCell>
                  <TableCell><Badge className="bg-slate-100 text-slate-900">{s.score}</Badge></TableCell>
                  <TableCell><Badge className={tierColor[s.tier] || ''}>{s.tier.replace('_',' ')}</Badge></TableCell>
                  <TableCell className="text-xs">{s.factors.attendance ?? '—'}</TableCell>
                  <TableCell className="text-xs">{s.factors.engagement ?? '—'}</TableCell>
                  <TableCell className="text-xs">{s.factors.exam ?? '—'}</TableCell>
                  <TableCell className="text-xs">{s.pending_pop_quizzes}</TableCell>
                </TableRow>
              ))}
            </TableBody>
           </Table>}
        </CardContent>
      </Card>
    </div>
  );
};

// ========== TEACHER PERFORMANCE ==========
const TeacherPerfTab = ({ tenantId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['rep-tp', tenantId],
    queryFn: () => api.get(`/api/school/tenants/${tenantId}/reports/teacher-performance`).then(r => r.data),
  });
  const tierColor = { excellent: 'bg-emerald-100 text-emerald-800', good: 'bg-indigo-100 text-indigo-800', needs_improvement: 'bg-amber-100 text-amber-800', at_risk: 'bg-rose-100 text-rose-800' };
  const d = data || {};
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-5 gap-3">
        <div className="p-3 rounded-lg border bg-slate-50"><div className="text-xs">Average</div><div className="text-2xl font-bold" data-testid="tp-avg">{d.average_score || 0}</div></div>
        <div className="p-3 rounded-lg border bg-emerald-50"><div className="text-xs text-emerald-700">Excellent</div><div className="text-2xl font-bold text-emerald-900" data-testid="tp-excellent">{d.by_tier?.excellent || 0}</div></div>
        <div className="p-3 rounded-lg border bg-indigo-50"><div className="text-xs text-indigo-700">Good</div><div className="text-2xl font-bold text-indigo-900" data-testid="tp-good">{d.by_tier?.good || 0}</div></div>
        <div className="p-3 rounded-lg border bg-amber-50"><div className="text-xs text-amber-700">Needs Improv.</div><div className="text-2xl font-bold text-amber-900" data-testid="tp-ni">{d.by_tier?.needs_improvement || 0}</div></div>
        <div className="p-3 rounded-lg border bg-rose-50"><div className="text-xs text-rose-700">At Risk</div><div className="text-2xl font-bold text-rose-900" data-testid="tp-risk">{d.by_tier?.at_risk || 0}</div></div>
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">All Teachers</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> :
           <Table>
            <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead>Score</TableHead><TableHead>Tier</TableHead><TableHead>Att%</TableHead><TableHead>Syl%</TableHead><TableHead>Eng</TableHead><TableHead>Rev</TableHead></TableRow></TableHeader>
            <TableBody>
              {(d.teachers || []).map(t => (
                <TableRow key={t.teacher_id} data-testid={`tp-row-${t.teacher_id}`}>
                  <TableCell className="font-medium text-sm">{t.teacher_name.trim() || '—'}</TableCell>
                  <TableCell><Badge className="bg-slate-100 text-slate-900">{t.composite_score ?? '—'}</Badge></TableCell>
                  <TableCell><Badge className={tierColor[t.tier] || ''}>{(t.tier || '').replace('_',' ')}</Badge></TableCell>
                  <TableCell className="text-xs">{t.factors.attendance ?? '—'}</TableCell>
                  <TableCell className="text-xs">{t.factors.syllabus ?? '—'}</TableCell>
                  <TableCell className="text-xs">{t.factors.engagement ?? '—'}</TableCell>
                  <TableCell className="text-xs">{t.factors.review ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
           </Table>}
        </CardContent>
      </Card>
    </div>
  );
};

// ========== EXPORTS ==========
const ExportsTab = ({ tenantId }) => {
  const [busy, setBusy] = useState('');
  const downloadCsv = async (report) => {
    setBusy(`csv-${report}`);
    try {
      const r = await api.get(`/api/school/tenants/${tenantId}/reports/export/csv?report=${report}`);
      const blob = new Blob([r.data.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${report}-${Date.now()}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${report} CSV downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
    finally { setBusy(''); }
  };
  const downloadPdf = async (report) => {
    setBusy(`pdf-${report}`);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/school/tenants/${tenantId}/reports/export/pdf?report=${report}`, {
        headers: { Authorization: `Bearer ${(getToken()) || getToken() || ''}` }
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `${report}-${Date.now()}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${report} PDF downloaded`);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(''); }
  };
  const REPORTS = [
    { id: 'executive', label: 'Executive Dashboard' },
    { id: 'students-at-risk', label: 'Students at Risk' },
    { id: 'teacher-performance', label: 'Teacher Performance' },
    { id: 'attendance-heatmap', label: 'Attendance Heatmap' },
  ];
  return (
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2 text-base"><FileDown className="h-4 w-4 text-emerald-600" /> Export Reports</CardTitle>
        <CardDescription>Download any report as CSV or a branded PDF for printing/sharing.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-3">
          {REPORTS.map(r => (
            <div key={r.id} className="flex items-center justify-between p-3 rounded-md border bg-slate-50" data-testid={`export-row-${r.id}`}>
              <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-500" /><span className="text-sm font-medium">{r.label}</span></div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => downloadCsv(r.id)} disabled={busy === `csv-${r.id}`} data-testid={`export-csv-${r.id}`}>
                  {busy === `csv-${r.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : 'CSV'}
                </Button>
                <Button size="sm" onClick={() => downloadPdf(r.id)} disabled={busy === `pdf-${r.id}`} data-testid={`export-pdf-${r.id}`}>
                  {busy === `pdf-${r.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Printer className="h-3.5 w-3.5 mr-1" /> PDF</>}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ========== MAIN ==========
export const SchoolReportsAnalytics = ({ tenantId }) => {
  return (
    <div className="space-y-4" data-testid="reports-analytics-panel">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Reports & Analytics</h2>
        <p className="text-sm text-slate-500">Executive KPIs · Heatmaps · Risk Scores · Teacher Performance · Exports</p>
      </div>
      <Tabs defaultValue="executive">
        <TabsList className="flex-wrap">
          <TabsTrigger value="executive" data-testid="rep-tab-executive"><BarChart3 className="h-4 w-4 mr-1" /> Executive</TabsTrigger>
          <TabsTrigger value="heatmap" data-testid="rep-tab-heatmap"><Thermometer className="h-4 w-4 mr-1" /> Heatmap</TabsTrigger>
          <TabsTrigger value="sar" data-testid="rep-tab-sar"><AlertTriangle className="h-4 w-4 mr-1" /> Students at Risk</TabsTrigger>
          <TabsTrigger value="tp" data-testid="rep-tab-tp"><GraduationCap className="h-4 w-4 mr-1" /> Teacher Performance</TabsTrigger>
          <TabsTrigger value="exports" data-testid="rep-tab-exports"><FileDown className="h-4 w-4 mr-1" /> Exports</TabsTrigger>
        </TabsList>
        <TabsContent value="executive" className="mt-4"><ExecutiveTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="heatmap" className="mt-4"><HeatmapTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="sar" className="mt-4"><StudentsAtRiskTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="tp" className="mt-4"><TeacherPerfTab tenantId={tenantId} /></TabsContent>
        <TabsContent value="exports" className="mt-4"><ExportsTab tenantId={tenantId} /></TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolReportsAnalytics;
